import { useMemo } from 'react'

import { useAuthStore } from '../store'
import {
  makePermissionChecker,
  resolveActivePermissions,
  type PermissionChecker,
} from './permissionUtils'

/**
 * Hook de permissions RBAC, escopado à organização ativa.
 *
 * Uso:
 *   const { has, hasAny } = usePermissions()
 *   {has('vms:create') && <CreateVMButton />}
 *   <button disabled={!has('billing:write')}>Adicionar cartão</button>
 */
export function usePermissions(): PermissionChecker {
  const user = useAuthStore((s) => s.user)
  const memberships = useAuthStore((s) => s.memberships)
  const activeOrganizationId = useAuthStore((s) => s.activeOrganizationId)

  return useMemo(() => {
    const perms = resolveActivePermissions(memberships, activeOrganizationId, user?.permissions)
    return makePermissionChecker(perms, !!user?.is_superuser)
  }, [user, memberships, activeOrganizationId])
}

/**
 * Atalho booleano. Passe uma string para uma permission, ou um array para
 * exigir PELO MENOS UMA (OR).
 */
export function useHasPermission(permission: string | string[]): boolean {
  const checker = usePermissions()
  return Array.isArray(permission) ? checker.hasAny(permission) : checker.has(permission)
}
